import type { Ref } from 'react';
import cn from 'classnames';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import IconTrash from '@creditflowresearch/uilib/Icon/IconTrash';
import Button from '@creditflowresearch/uilib/Button';
import Flex from '@creditflowresearch/uilib/Flex';
import TruncateText from '@creditflowresearch/uilib/TruncateText';
import { useAskAiChatListContext } from '@/creditflowresearch/application/AskAiChat/context/AskAiChatContext';
import styles from './chatHistoryList.module.scss';

interface IChatHistoryListProps {
  className?: string,
  onOpen?: () => void,
  dataTest: string,
  hasLoadMoreButton?: boolean,
  ref?: Ref<HTMLDivElement>,
}

const ChatHistoryList = ({
  className,
  onOpen,
  dataTest,
  hasLoadMoreButton = true,
  ref,
}: IChatHistoryListProps) => {
  const {
    chats,
    chatId,
    canSwitchChat,
    isChatListFetching,
    hasMoreChats,
    loadMoreChats,
    openChat,
    deleteChat,
  } = useAskAiChatListContext();

  const handleOpen = (id: string): void => {
    openChat(id);
    onOpen?.();
  };

  return (
    <Flex ref={ ref } direction="column" className={ cn(styles.list, className) } dataTest={ dataTest }>
      { !chats.length && !isChatListFetching && (
        <div className={ styles.empty }>No chats yet</div>
      ) }

      { chats.map((chat) => (
        <Flex
          key={ chat.id }
          align="center"
          gap={ 4 }
          className={ cn(styles.item, { [styles.itemActive]: chat.id === chatId }) }
        >
          <button
            type="button"
            className={ styles.itemTitle }
            onClick={ () => handleOpen(chat.id) }
            disabled={ !canSwitchChat }
            data-test="askAiChatHistoryItem"
          >
            <span className={ styles.itemTitleLabel }>
              <TruncateText value={ chat.title } />
            </span>
          </button>
          <Button
            className={ styles.deleteButton }
            variant="text"
            size="S"
            icon={ <IconTrash /> }
            iconSize={ 12 }
            onClick={ () => deleteChat(chat.id) }
            ariaLabel={ `Delete ${ chat.title }` }
            dataTest="askAiChatHistoryDelete"
          />
        </Flex>
      )) }

      { isChatListFetching && <Spinner size="XS" centered /> }

      { hasLoadMoreButton && hasMoreChats && !isChatListFetching && (
        <Button
          className={ styles.loadMore }
          variant="text"
          size="S"
          onClick={ () => loadMoreChats() }
          dataTest="askAiChatHistoryLoadMore"
        >
          Show more
        </Button>
      ) }
    </Flex>
  );
};

export default ChatHistoryList;
